<template>
  <div
    class="line-item"
    :class="[
      $blockClass(blockClass, 'line-item'),
      {
        [$class('loading')]: !item,
        [$class('updating')]: state === 'updating'
      },
    ]"
  >
    <a :href="item.url">
      <responsive-image
        :class="$blockClass(blockClass, 'line-item-image')"
        block-class="line-item"
        :image="item.featured_image"
        lazy-load
        min-max="122-144"
        ratio="0:0"
        sizes="122px"
      />
    </a>

    <div
      class="line-item__body"
      :class="$blockClass(blockClass, 'line-item-body')"
    >
      <div
        class="line-item__header"
        :class="$blockClass(blockClass, 'line-item-header')"
      >
        <a
          class="line-item__title"
          :class="$blockClass(blockClass, 'line-item-title')"
          :href="item.url"
          v-text="getProductTitle"
        />

        <p
          v-if="!item.product_has_only_default_variant"
          class="line-item__variant"
          :class="$blockClass(blockClass, 'line-item-variant')"
          v-text="item.gift_card ? $formatMoney(item.original_price) : item.variant_title"
        />

        <product-prices
          block-class="line-item"
          class="line-item__price"
          :class="$blockClass(blockClass, 'line-item-prices')"
          :compare-at-price="item?.compare_at_price || false"
          :price="item?.final_price"
          :show-message="false"
        />
      </div>

      <p
        v-if="isFinalSale && showFinalSaleMessage"
        class="line-item__final-sale-message"
      >
        <span v-text="$string('cart.line_item.final_sale_message')" />
      </p>

      <div
        class="line-item__footer"
        :class="$blockClass(blockClass, 'line-item-footer')"
      >
        <quantity-selector
          :class="$blockClass(blockClass, 'line-item-quantity')"
          block-class="line-item"
          :item="item"
          :quantity="quantity"
          :state="state"
          @update-quantity="handleQuantityUpdate"
        />

        <button
          class="line-item__remove"
          :class="[
            $blockClass(blockClass, 'line-item-remove'),
            { [$class('disabled')]: state === 'updating' },
          ]"
          type="button"
          @click="handleRemove"
        >
          <span v-text="$string('cart.line_item.remove')" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean|Object} item - Line item in cart response or order.
 *
 */
import { mapActions, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'LineItem',

  components: {
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    item: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    showFinalSaleMessage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      quantity: 1,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute if the current line item is tagged as "finalsale".
     * @returns {Boolean}
     */
    isFinalSale() {
      return Array.isArray(this.item?.tags) && this.item.tags.includes('finalsale')
    },

    /**
     * Compute product title.
     * @returns {String}
     */
    getProductTitle() {
      let title = this.item.product_title

      if (title.includes(' - ')) {
        title = title.split(' - ')[0]
      }

      return title
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.item.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'item.quantity'() {
      this.quantity = this.item.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.item.quantity
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.item.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'

      } catch (error) {
        this.quantity = this.item.quantity
        this.state = 'error'
      }
    },

    /**
     * Handle remove click.
     */
    handleRemove() {
      if (this.state === 'updating') {
        return
      }

      this.removeItem(this.item.key)
    },
  },
}
</script>
